import { Refine } from '@refinedev/core';
import {
  ThemedLayoutV2,
  notificationProvider,
} from '@refinedev/antd';
import { DataProvider } from '@refinedev/core';
import routerProvider from '@refinedev/react-router-v6';
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ConfigProvider from 'antd/es/config-provider';
import theme from 'antd/es/theme';
import { lazy, Suspense } from 'react';
import { getApiUrl } from './lib/config';

// Lazy load pages
const LobbyList = lazy(() => import('./pages/lobby/list'));
const GameCreate = lazy(() => import('./pages/lobby/create'));
const SinglePlayerCreate = lazy(() => import('./pages/lobby/single-player'));
const PlayHub = lazy(() => import('./pages/lobby/play-hub'));
const PlayAI = lazy(() => import('./pages/lobby/play-ai'));
const PlayPvP = lazy(() => import('./pages/lobby/play-pvp'));
const GameJoin = lazy(() => import('./pages/lobby/join'));
const BasicPlay = lazy(() => import('./pages/game/basic-play'));
const MultiplayerPlay = lazy(() => import('./pages/game/play'));
const RulesPage = lazy(() => import('./pages/RulesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const TournamentPage = lazy(() => import('./pages/TournamentPage'));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage'));
const BlogCategoryPage = lazy(() => import('./pages/blog/BlogCategoryPage'));
const BlogLevelPage = lazy(() => import('./pages/blog/BlogLevelPage'));
const BlogArticlePage = lazy(() => import('./pages/blog/BlogArticlePage'));
const BlogTagPage = lazy(() => import('./pages/blog/BlogTagPage'));
const CertificatePage = lazy(() => import('./pages/CertificatePage'));
const ChallengeLandingPage = lazy(() => import('./pages/ChallengeLandingPage'));
const ReplayPage = lazy(() => import('./pages/game/ReplayPage'));
const ForResearchersPage = lazy(() => import('./pages/ForResearchersPage'));
const ResearchEthicsPage = lazy(() => import('./pages/ResearchEthicsPage'));
const ForMilitaryPage = lazy(() => import('./pages/ForMilitaryPage'));
const ForEducatorsPage = lazy(() => import('./pages/ForEducatorsPage'));
const PressPage = lazy(() => import('./pages/PressPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const StatsPage = lazy(() => import('./pages/StatsPage'));
const ScenarioHubPage = lazy(() => import('./pages/scenarios/ScenarioHubPage'));
const ScenarioDetailPage = lazy(() => import('./pages/scenarios/ScenarioDetailPage'));
const GlossaryHubPage = lazy(() => import('./pages/glossary/GlossaryHubPage'));
const GlossaryTermPage = lazy(() => import('./pages/glossary/GlossaryTermPage'));
const LearnPage = lazy(() => import('./pages/learn/LearnPage'));
const QuizPage = lazy(() => import('./pages/learn/QuizPage'));
const LearnArticlePage = lazy(() => import('./pages/learn/LearnArticlePage'));
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'));
const AdminTournamentsPage = lazy(() => import('./pages/AdminTournamentsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const SpectatorPage = lazy(() => import('./pages/game/SpectatorPage'));

// Components — AppHeader is lazy-loaded so it never blocks the landing page bundle
const AppHeader = lazy(() => import('./components/AppHeader'));
import AppFooter from './components/AppFooter';
import BreadcrumbNav from './components/BreadcrumbNav';
import { GameLoadingSpinner } from './components/ui/LoadingSpinner';
import FeedbackButton from './components/FeedbackButton';

// Scroll to top on every route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Storage key constants
const PLAYER_ID_KEY = 'tth_player_id';

// Helper to get player ID from localStorage
const getPlayerId = () => localStorage.getItem(PLAYER_ID_KEY);

// Data provider implementation
const dataProvider: DataProvider = {
  create: async ({ resource, variables }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const response = await fetch(`${apiUrl}/${resource}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to create ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  createMany: async ({ resource, variables }) => {
    const results = await Promise.all(
      variables.map((v) => dataProvider.create({ resource, variables: v }))
    );
    return { data: results.map((r) => r.data) } as never;
  },

  deleteOne: async ({ resource, id }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const response = await fetch(`${apiUrl}/${resource}/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  deleteMany: async ({ resource, ids }) => {
    const results = await Promise.all(
      ids.map((id) => dataProvider.deleteOne({ resource, id }))
    );
    return { data: results.map((r) => r.data) } as never;
  },

  getList: async ({ resource, filters }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const url = new URL(`${apiUrl}/${resource}`, window.location.origin);

    // Add query params for filtering
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if ('field' in filter && 'operator' in filter && 'value' in filter) {
          url.searchParams.set(filter.field, String(filter.value));
        }
      });
    }

    const response = await fetch(url.toString(), {
      credentials: 'include',
      headers: {
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${resource}`);
    }

    const data = await response.json();
    return data;
  },

  getMany: async ({ resource, ids }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const promises = ids.map((id) =>
      fetch(`${apiUrl}/${resource}/${id}`, {
        credentials: 'include',
        headers: {
          ...(playerId ? { 'X-Player-ID': playerId } : {}),
        },
      })
    );

    const responses = await Promise.all(promises);
    const data = await Promise.all(responses.map((r) => r.json()));

    return { data };
  },

  getOne: async ({ resource, id }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const response = await fetch(`${apiUrl}/${resource}/${id}`, {
      credentials: 'include',
      headers: {
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to get ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  update: async ({ resource, id, variables, meta }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    // For patch operations like /players/:id/game
    const url = meta?.patchEndpoint
      ? `${apiUrl}/${resource}/${id}/${meta.patchEndpoint}`
      : `${apiUrl}/${resource}/${id}`;

    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const results = await Promise.all(
      ids.map((id) => dataProvider.update({ resource, id, variables }))
    );
    return { data: results.map((r) => r.data) } as never;
  },

  custom: async ({ url, method, headers, payload, query, meta }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const fullUrl = url ?? `${apiUrl}/${meta?.resource ?? ''}`;

    const queryParams = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.set(key, String(value));
        }
      });
    }

    const urlWithQuery = queryParams.toString()
      ? `${fullUrl}?${queryParams.toString()}`
      : fullUrl;

    const response = await fetch(urlWithQuery, {
      method: method || 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
        ...headers,
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Request failed');
    }

    const data = await response.json();
    return { data };
  },

  getApiUrl: () => getApiUrl(),
};

// Loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
    <GameLoadingSpinner />
  </div>
);

// Reads the language from the URL path prefix and keeps i18n in sync.
// /uk/* → 'uk', /pl/* → 'pl', /de/* → 'de', /fr/* → 'fr', anything else → 'en'.
//
// Sticky language: if the user is in a non-English language but a hardcoded
// link inside the app navigates to an English URL (no prefix), we re-add the
// prefix instead of silently downgrading to English. This covers the many
// call sites that still do `navigate('/blog')` without langPrefix.
const LanguagePathSync: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { pathname, search, hash } = location;

  React.useEffect(() => {
    const m = pathname.match(/^\/(uk|pl|de|fr)(\/|$)/);
    const pathLang = m ? m[1]! : 'en';

    if (pathLang === 'en' && i18n.language !== 'en' && ['uk', 'pl', 'de', 'fr'].includes(i18n.language)) {
      // Re-prepend the active language prefix so navigation stays in-language.
      navigate(`/${i18n.language}${pathname === '/' ? '' : pathname}${search}${hash}`, { replace: true });
      return;
    }

    if (i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
  }, [pathname, search, hash, i18n, navigate]);

  return null;
};

// Catch-all fallback — redirects to lobby preserving language prefix
const LangAwareFallback: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  React.useEffect(() => {
    const prefix = i18n.language !== 'en' ? `/${i18n.language}` : '';
    navigate(`${prefix}/lobby`, { replace: true });
  }, [navigate, i18n.language]);
  return null;
};

// Animated page wrapper — remounts on route change to trigger CSS animation
const AnimatedOutlet: React.FC = () => {
  const { key } = useLocation();
  return <div key={key} className="page-enter"><Outlet /></div>;
};

// Shows the floating FeedbackButton everywhere except on game pages
// (there it's replaced by an inline button inside the Control Panel to avoid
// overlapping the TacticalOddsBar at the bottom-right of the panel)
const GlobalFeedbackButton: React.FC = () => {
  const { pathname } = useLocation();
  const isGamePage = /\/game\//.test(pathname);
  if (isGamePage) return null;
  return <FeedbackButton />;
};

// App layout with header/footer (stable references to avoid remounting)
const SiderNull = () => null;
const HeaderComp = () => <AppHeader />;
const FooterComp = () => <AppFooter />;

const AppLayout: React.FC = () => (
  <ThemedLayoutV2 Sider={SiderNull} Header={HeaderComp} Footer={FooterComp}>
    <BreadcrumbNav />
    <AnimatedOutlet />
  </ThemedLayoutV2>
);

// Shared route tree (relative paths) — rendered for EN, UK, and PL trees.
// Routes use relative paths so they resolve correctly under any language prefix.
const sharedRoutes = () => (
  <>
    <Route index element={<LandingPage />} />
    <Route element={<AppLayout />}>
      <Route path="lobby" element={<LobbyList />} />
      <Route path="lobby/create" element={<GameCreate />} />
      <Route path="play" element={<PlayHub />} />
      <Route path="play/ai" element={<PlayAI />} />
      <Route path="play/pvp" element={<PlayPvP />} />
      <Route path="single-player" element={<SinglePlayerCreate />} />
      <Route path="lobby/join/:gameId" element={<GameJoin />} />
      <Route path="lobby/game/:gameId" element={<GameJoin />} />
      <Route path="rules" element={<RulesPage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="changelog" element={<ChangelogPage />} />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="leaderboard" element={<LeaderboardPage />} />
      <Route path="tournament" element={<TournamentPage />} />
      <Route path="api-docs" element={<ApiDocsPage />} />
      <Route path="game/basic/:gameId?" element={<BasicPlay />} />
      <Route path="game/:gameId" element={<MultiplayerPlay />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="blog" element={<BlogIndexPage />} />
      <Route path="blog/tag/:tag" element={<BlogTagPage />} />
      <Route path="blog/level/:difficulty" element={<BlogLevelPage />} />
      <Route path="blog/:category" element={<BlogCategoryPage />} />
      <Route path="blog/:category/:slug" element={<BlogArticlePage />} />
      <Route path="certificate/:id" element={<CertificatePage />} />
      <Route path="challenge/:id" element={<ChallengeLandingPage />} />
      <Route path="replay/:gameId" element={<ReplayPage />} />
      <Route path="for-researchers" element={<ForResearchersPage />} />
      <Route path="research/ethics" element={<ResearchEthicsPage />} />
      <Route path="for-military" element={<ForMilitaryPage />} />
      <Route path="for-educators" element={<ForEducatorsPage />} />
      <Route path="press" element={<PressPage />} />
      <Route path="faq" element={<FaqPage />} />
      <Route path="stats" element={<StatsPage />} />
      <Route path="scenarios" element={<ScenarioHubPage />} />
      <Route path="scenarios/:slug" element={<ScenarioDetailPage />} />
      <Route path="glossary" element={<GlossaryHubPage />} />
      <Route path="glossary/:slug" element={<GlossaryTermPage />} />
      <Route path="learn" element={<LearnPage />} />
      <Route path="learn/quiz" element={<QuizPage />} />
      <Route path="learn/*" element={<LearnArticlePage />} />
      <Route path="admin/feedback" element={<AdminFeedbackPage />} />
      <Route path="admin/tournaments" element={<AdminTournamentsPage />} />
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="spectate/:gameId" element={<SpectatorPage />} />
      <Route path="*" element={<LangAwareFallback />} />
    </Route>
  </>
);

const ThemedApp: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 4,
        },
      }}
    >
      <Refine
          dataProvider={dataProvider}
          routerProvider={routerProvider}
          notificationProvider={notificationProvider}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Ukrainian — /uk/* */}
              <Route path="uk">
                {sharedRoutes()}
              </Route>
              {/* Polish — /pl/* */}
              <Route path="pl">
                {sharedRoutes()}
              </Route>
              {/* German — /de/* */}
              <Route path="de">
                {sharedRoutes()}
              </Route>
              {/* French — /fr/* */}
              <Route path="fr">
                {sharedRoutes()}
              </Route>
              {/* English (default) — /* */}
              {sharedRoutes()}
            </Routes>
          </Suspense>
        </Refine>
    </ConfigProvider>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <LanguagePathSync />
      <GlobalFeedbackButton />
      <ThemedApp />
    </BrowserRouter>
  );
};

export default App;
